/**
 * My account — `design-remediation.md` WP2, drawn from
 * `21-catalog-packages/html/packages-account.html`.
 *
 * Everything about *you*, in one scrolling column: who you are, how you get
 * in, what reaches you, what you have worked, and how the app behaves for you.
 *
 * It used to define a `Field` and a `KV` of its own — a 11px uppercase label
 * and a key/value line that shared nothing with the rest of the product — and
 * the hours card was a paragraph where four numbers belong. The cards are
 * `SettingsCard`/`SettingRow` now, the fields are the shared `Field`, and the
 * only thing this file draws itself is the notification matrix, because
 * nothing else in the product is that shape.
 *
 * **The column scrolls; it is not a fixed-height flex column.** The cards
 * vary: a longer device list or a taller font has to grow the page, not have
 * its last card clipped. Every card is `shrink-0` and the column is a plain
 * `overflow-y-auto`.
 *
 * What is *not* editable here is as much the point as what is. Role and branch
 * access say what somebody may reach, so letting them widen their own would
 * make the permission matrix a suggestion; they are shown, and they say who
 * does change them. The email is an identity rather than a field.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type {
  MeResponse, MfaKind, MyNotificationChannel, MyNotificationKind,
} from '@rowted/shared'
import {
  MY_NOTIFICATION_BLURBS, MY_NOTIFICATION_CHANNELS, MY_NOTIFICATION_CHANNEL_LABELS,
  MY_NOTIFICATION_KINDS, MY_NOTIFICATION_LABELS, describeAgent, quietHoursLabel,
  resolveMatrix,
  type MyNotificationPrefs,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { readTheme } from '../lib/theme.js'
import { PageBar } from '../frame/page-bar.js'
import {
  Avatar, Band, Button, Checkbox, StatStrip, Tag, type Stat,
} from '../components/primitives.js'
import { Modal } from '../components/overlays.js'
import { SettingsCard, SettingRow } from '../components/settings-card.js'
import { SignInMethods, type MethodsAnswer } from '../components/sign-in-methods.js'
import { EnvironmentsCard } from '../components/environments-card.js'
import { useEnvironments } from '../frame/use-environments.js'
import { Field } from '../components/fields.js'
import { MenuSelect } from '../components/menu-select.js'
import {
  IconClock, IconHome, IconMaps, IconMonitor, IconMoon, IconPinCode,
} from '../frame/icons.js'
import { formatNumber } from '../lib/format.js'
import { AccountSecurityEditor, type SecurityAction } from '../account/security-editors.js'
import { TwoStepSetup } from '../components/two-step-setup.js'
import { TenantAccountApi } from '../account/providers.js'
import { ActivityDevicesCard, EmailCard } from '../account/activity-cards.js'

interface SessionRow {
  id: string
  surface: string
  ip: string | null
  userAgent: string | null
  createdAt: string
  lastSeenAt: string
  current: boolean
}

interface HoursWeek { totalMinutes: number; jobMinutes: number }

/**
 * The provider, outside the page — `account/account-api.tsx`.
 *
 * The Sign-in methods card, the Environments card and the setup dialog are
 * the same components Rowted Admin's profile draws (`pages/admin/account.tsx`);
 * what tells them which door to knock on is this context, and it carries the
 * step-up dialog with it so nothing below has to render one.
 */
export function Account({ me, onSaved }: { me: MeResponse; onSaved?: () => void }) {
  return (
    <TenantAccountApi>
      <AccountPage me={me} onSaved={onSaved} />
    </TenantAccountApi>
  )
}

function AccountPage({ me, onSaved }: { me: MeResponse; onSaved?: () => void }) {
  const navigate = useNavigate()

  const [firstName, setFirstName] = useState(me.user.firstName ?? '')
  const [lastName, setLastName] = useState(me.user.lastName ?? '')
  const [title, setTitle] = useState(me.user.title ?? '')
  const [phone, setPhone] = useState(me.user.phone ?? '')
  const [prefs, setPrefs] = useState<MyNotificationPrefs>(me.user.notificationPrefs)

  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  const [sessions, setSessions] = useState<SessionRow[] | null>(null)
  const [thisWeek, setThisWeek] = useState<HoursWeek | null>(null)
  const [lastWeek, setLastWeek] = useState<HoursWeek | null>(null)

  /** Which security flow is open, and the button it hangs off. */
  const [security, setSecurity] = useState<SecurityAction | null>(null)
  /*
   * Two-step is a list of methods now (§4). The row summarises what the
   * account holds and the dialog is where they are added or removed — the old
   * `security === 'mfa'` editor only ever knew about an authenticator, which
   * is why its row could only ever read "On · authenticator app".
   */
  /* `null` is closed; a kind opens straight into that method — **Reset** on
     the authenticator row, and **Add another** on a passkey. */
  const [twoStep, setTwoStep] = useState<{ begin?: MfaKind } | null>(null)
  /*
   * The whole answer, not just the list: the Sign-in methods card needs the
   * policy (can the last one come off?) and the destinations (where would a
   * code go?), and two reads of the same endpoint would be two answers.
   */
  const [methodsAnswer, setMethodsAnswer] = useState<MethodsAnswer | null>(null)
  const { environments, current, reload: reloadEnvironments } = useEnvironments(request)
  const [confirmSignOut, setConfirmSignOut] = useState(false)

  useEffect(() => {
    /*
     * `null` means not known, and stays null when the read fails or comes back
     * without a list. It used to fall back to `[]`, which made the row read
     * "Off" — a worse answer than no answer, because somebody who does hold a
     * passkey would have been told they hold nothing.
     */
    void request<MethodsAnswer>('/me/methods')
      .then((answer) => setMethodsAnswer(Array.isArray(answer.methods) ? answer : null))
      .catch(() => setMethodsAnswer(null))
  }, [])
  const securityAt = useRef<HTMLDivElement>(null)

  const loadSessions = useCallback(async () => {
    try {
      const body = await request<{ sessions: SessionRow[] }>('/me/sessions')
      setSessions(body.sessions)
    } catch {
      setSessions([])
    }
  }, [])

  useEffect(() => { void loadSessions() }, [loadSessions])

  /*
   * This week and the one before it. Two calls rather than one, because the
   * endpoint answers for a week and "last week" is a different week — and a
   * strip that showed one number twice would be worse than a strip with a
   * dash in it.
   */
  useEffect(() => {
    let live = true
    const before = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
    void request<HoursWeek>('/crew-app/my-hours')
      .then((body) => { if (live) setThisWeek(body) })
      .catch(() => { if (live) setThisWeek({ totalMinutes: 0, jobMinutes: 0 }) })
    void request<HoursWeek>(`/crew-app/my-hours?week=${before}`)
      .then((body) => { if (live) setLastWeek(body) })
      .catch(() => { if (live) setLastWeek({ totalMinutes: 0, jobMinutes: 0 }) })
    return () => { live = false }
  }, [])

  const profileDirty = firstName !== (me.user.firstName ?? '')
    || lastName !== (me.user.lastName ?? '')
    || title !== (me.user.title ?? '')
    || phone !== (me.user.phone ?? '')
  const prefsDirty = JSON.stringify(prefs) !== JSON.stringify(me.user.notificationPrefs)
  const dirty = profileDirty || prefsDirty

  async function save() {
    setBusy(true)
    setProblem(null)
    try {
      if (profileDirty) {
        await request('/me', { method: 'PATCH', body: { firstName, lastName, title, phone } })
      }
      if (prefsDirty) {
        await request('/me/notification-prefs', { method: 'PUT', body: prefs })
      }
      setNotice('Saved.')
      onSaved?.()
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'That did not save.')
    } finally {
      setBusy(false)
    }
  }

  async function signOutOthers() {
    setBusy(true)
    try {
      const body = await request<{ revoked: number }>(
        '/me/sessions/revoke-others', { method: 'POST' })
      setNotice(body.revoked === 0
        ? 'You were not signed in anywhere else.'
        : `Signed out ${body.revoked} other ${body.revoked === 1 ? 'device' : 'devices'}.`)
      await loadSessions()
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Board 2's **Sign out everywhere**.
   *
   * Confirmed, because it takes *this* device too — and unconfirmed it sits
   * one mis-click from Save changes. Afterwards the page is reloaded rather
   * than routed: the cookie is gone, so every request from here answers 401,
   * and the sign-in page is the only honest next screen.
   */
  async function signOutEverywhere() {
    setBusy(true)
    try {
      await request('/me/sessions/revoke-all', { method: 'POST' })
      window.location.assign('/sign-in')
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'That did not work.')
      setBusy(false)
    }
  }

  const branches = me.scope.available.filter(
    (unit) => unit.reachable && unit.kind === 'branch')
  const matrix = useMemo(() => resolveMatrix(prefs), [prefs])

  /** One box in the matrix, written back as an override of the default. */
  const setChannel = (
    kind: MyNotificationKind, channel: MyNotificationChannel, on: boolean,
  ) => setPrefs((current) => ({
    ...current,
    matrix: { ...current.matrix, [kind]: { ...current.matrix?.[kind], [channel]: on } },
  }))

  const roleNames = me.roles.map((role) => role.name).join(', ')
  const devices = sessions ?? []

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageBar
        title="My account"
        sub={[me.user.name, roleNames, me.scope.label].filter(Boolean).join(' · ')}
        actions={(
          <>
            {/*
              * Board 2's page-bar action, and deliberately not beside the
              * devices card: it is the thing you press when you think somebody
              * has your password, and it has to be findable without reading
              * the page. Confirmed, because it signs *this* device out too.
              */}
            <Button
              className="h-[34px]"
              disabled={busy}
              onClick={() => setConfirmSignOut(true)}
            >
              Sign out everywhere
            </Button>
            <Button
              variant="primary"
              className="h-[34px]"
              disabled={!dirty || busy}
              onClick={() => void save()}
            >
              {busy ? 'Saving…' : 'Save changes'}
            </Button>
          </>
        )}
      />

      {/* A normal scrolling column — see the note at the top of the file. */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {/*
          * Left-aligned in the content area, not centred: the mockup's column
          * is `max-width: 880px` against the page's own 24px gutter, and
          * `mx-auto` floated it in the middle with a gutter of empty on the
          * left that nothing else in the product has.
          */}
        <div className="flex w-full max-w-[880px] flex-col gap-4 px-6 pb-6 pt-5">
          {problem && <Band tone="amber">{problem}</Band>}
          {notice && !problem && <Band tone="green">{notice}</Band>}

          {/* --- 1. Profile ------------------------------------------------ */}
          <SettingsCard title="Profile" sub="how you appear to your team and on customer messages">
            <div className={cn('grid grid-cols-[88px_minmax(0,1fr)] items-start gap-[18px]',
              'px-[18px] pb-[18px] pt-4')}>
              <div className="flex flex-col items-center gap-2">
                <Avatar name={me.user.name} self size={72} />
                <button
                  type="button"
                  // Photos are a contact's feature today; this says where it
                  // will live rather than pretending the button works.
                  disabled
                  title="Not built yet"
                  className="text-[12px] font-semibold text-accent disabled:opacity-50"
                >
                  Change photo
                </button>
              </div>

              <div className="grid grid-cols-1 gap-x-3.5 gap-y-3 sm:grid-cols-2">
                <Field label="First name" value={firstName} onChange={setFirstName} required />
                <Field label="Last name" value={lastName} onChange={setLastName} />
                <Field label="Title" value={title} onChange={setTitle}
                  placeholder="Account manager" />
                <Field label="Mobile" value={phone} onChange={setPhone}
                  hint="Where a two-step code by text would go." />

                <ReadOnlyField label="Email" value={me.user.email} after="verified" />
                <ReadOnlyField
                  label="Role"
                  value={roleNames || 'No role yet'}
                  hint="set by an admin · People → Users & roles"
                />

                <div className="sm:col-span-2">
                  <span className="mb-1 block text-sub text-text-muted">Branch access</span>
                  <div className="flex min-h-9 flex-wrap items-center gap-1.5">
                    {branches.length === 0
                      ? <span className="text-cell text-text-faint">No branches yet</span>
                      : branches.slice(0, 10).map((unit) => (
                        <span
                          key={unit.id}
                          className={cn('inline-flex h-7 items-center rounded-chip border',
                            'border-accent-line bg-accent-soft px-2.5 text-[12.5px]',
                            'font-semibold text-accent-dark')}
                        >
                          {unit.name}
                        </span>
                      ))}
                    {branches.length > 10 && <Tag>+{branches.length - 10} more</Tag>}
                  </div>
                  <span className="mt-1 block text-sub text-text-faint">
                    {branches.length > 0
                      ? `all ${formatNumber(branches.length)} branches · `
                      : ''}
                    admins change this under People → Users &amp; roles
                  </span>
                </div>
              </div>
            </div>
          </SettingsCard>

          {/* --- 2. Security -----------------------------------------------
            *
            * `flex flex-col gap-4`, and that is a fix rather than a style: the
            * three cards were one flex child of the column above, so the
            * column's own `gap-4` fell between the Profile card and this
            * wrapper and nowhere inside it. Aaron: "there is some spacing
            * issues between three sections sing in, environments and this
            * device and this crew."
            */}
          <div ref={securityAt} className="flex flex-col gap-4">
            {/*
              * Two rows now — a password and a two-step line with a chip per
              * method. The six-row card it replaces is the right shape for a
              * screen *about* two-step and the wrong one for a profile; the
              * detail lives in the dialog **Manage** opens, which is the same
              * dialog the first-time walkthrough uses.
              */}
            <SignInMethods
              answer={methodsAnswer}
              recoveryCodesLeft={me.user.recoveryCodesLeft}
              passwordChangedAt={me.user.passwordChangedAt}
              onChangePassword={() => setSecurity('password')}
              onManage={() => setTwoStep({})}
            />

            {/* Board 2 puts this in the rail; the office profile is one
                column of cards, so it sits between the methods it is about
                and the devices those methods signed in. */}
            {environments.length > 1 && (
              <EnvironmentsCard
                environments={environments}
                current={current}
                ownEmail={me.user.email}
                onRemoved={reloadEnvironments}
              />
            )}

            <SettingsCard title="This device and this crew">
              <SettingRow
                icon={<IconPinCode size={17} />}
                label="Crew PIN"
                sub={me.user.pinExpected
                  ? 'only for clocking in on the crew iPad'
                  : 'only for clocking in on the crew iPad · you are not on a crew'}
                value={me.user.hasPin ? 'set' : 'not set'}
                action={(
                  <Button className="h-[30px]" onClick={() => setSecurity('pin')}>
                    {me.user.hasPin ? 'Change' : 'Set a PIN'}
                  </Button>
                )}
              />
              <SettingRow
                icon={<IconMonitor size={17} />}
                label="Signed-in devices"
                sub={devices.length === 0
                  ? 'looking…'
                  : devices.map((session) => describeAgent(session.userAgent)
                    + (session.current ? ' (this one)' : '')).join(' · ')}
                value={`${formatNumber(devices.length)} active`}
                action={(
                  <Button
                    className="h-[30px]"
                    disabled={busy || devices.length < 2}
                    onClick={() => void signOutOthers()}
                  >
                    Sign out others
                  </Button>
                )}
              />
            </SettingsCard>
          </div>

          {/* --- 3. Notifications ------------------------------------------ */}
          <SettingsCard title="Notifications" sub="what reaches you, and where">
            <div className={cn('grid grid-cols-[minmax(0,1fr)_70px_70px_70px_70px] gap-2',
              'border-b border-line-faint bg-surface-alt px-[18px] py-2 text-[10.5px]',
              'font-semibold uppercase tracking-[0.05em] text-text-faint')}>
              <span />
              {MY_NOTIFICATION_CHANNELS.map((channel) => (
                <span key={channel} className="text-center">
                  {MY_NOTIFICATION_CHANNEL_LABELS[channel]}
                </span>
              ))}
            </div>

            {MY_NOTIFICATION_KINDS.map((kind) => (
              <div
                key={kind}
                className={cn('grid grid-cols-[minmax(0,1fr)_70px_70px_70px_70px] items-center',
                  'gap-2 border-b border-line-faint px-[18px] py-2.5 text-[13px]')}
              >
                <div className="flex min-w-0 flex-col gap-px">
                  <span className="truncate text-text">{MY_NOTIFICATION_LABELS[kind]}</span>
                  <span className="truncate text-[11.5px] text-text-faint">
                    {MY_NOTIFICATION_BLURBS[kind]}
                  </span>
                </div>
                {MY_NOTIFICATION_CHANNELS.map((channel) => (
                  <span key={channel} className="flex justify-center">
                    <Checkbox
                      label={`${MY_NOTIFICATION_LABELS[kind]} by `
                        + MY_NOTIFICATION_CHANNEL_LABELS[channel]}
                      hideLabel
                      checked={matrix[kind][channel]}
                      /*
                       * A text with no number to send it to is a preference
                       * that cannot be honoured, so the box says why rather
                       * than accepting a setting nothing will act on.
                       */
                      disabled={channel === 'sms' && !phone.trim()}
                      title={channel === 'sms' && !phone.trim()
                        ? 'Add a mobile number above first'
                        : undefined}
                      onChange={(event) => setChannel(kind, channel, event.target.checked)}
                    />
                  </span>
                ))}
              </div>
            ))}

            <div className={cn('flex items-center gap-2 border-t border-line-faint px-[18px]',
              'py-3 text-[12.5px] text-text-muted')}>
              <span>
                Quiet hours <b className="text-text">{quietHoursLabel(prefs.quietHours)}</b>
                {prefs.quietHours ? ' · push and SMS wait until morning' : ''}
              </span>
              <span className="ml-auto">
                <MenuSelect
                  value={prefs.quietHours ? `${prefs.quietHours.from}-${prefs.quietHours.to}` : ''}
                  options={QUIET_WINDOWS}
                  onChange={(value) => setPrefs((current) => ({
                    ...current,
                    quietHours: value
                      ? { from: value.split('-')[0]!, to: value.split('-')[1]! }
                      : null,
                  }))}
                  size="small"
                  label="Quiet hours"
                />
              </span>
            </div>
          </SettingsCard>

          {/* --- Activity's two (`docs/activity.md` §12.4, §12.5) --------- */}
          <EmailCard me={me} />
          <ActivityDevicesCard me={me} />

          {/* --- 4. My hours ----------------------------------------------- */}
          <SettingsCard title="My hours" sub="this week · from your punches">
            <div className="px-[18px] py-4">
              <StatStrip
                stats={hourStats(thisWeek, lastWeek)}
                action={(
                  <Button className="h-[30px]" onClick={() => navigate('/my-hours')}>
                    <IconClock size={15} />
                    All my hours
                  </Button>
                )}
              />
            </div>
          </SettingsCard>

          {/* --- 5. Preferences -------------------------------------------- */}
          <SettingsCard title="Preferences">
            <SettingRow
              icon={<IconMoon size={17} />}
              label="Appearance"
              sub="or set it from the user menu"
              value={THEME_LABELS[readTheme()]}
            />
            <SettingRow
              icon={<IconMaps size={17} />}
              label="Scope when you sign in"
              sub="the branch picker at the top of the window, which remembers itself"
              value={me.scope.label}
            />
            <SettingRow
              icon={<IconHome size={17} />}
              label="Start page"
              sub="set by your role"
              value={me.defaultApp === 'crew_app' ? 'The crew app' : 'The office app'}
            />
            <SettingRow
              icon={<IconClock size={17} />}
              label="Time zone"
              sub="from your branch · an admin changes it under Organization"
              value={me.user.timeZone.replace('_', ' ').replace('/', ' / ')}
            />
          </SettingsCard>
        </div>
      </div>

      {confirmSignOut && (
        <Modal
          open
          onClose={() => setConfirmSignOut(false)}
          width={420}
          title="Sign out everywhere?"
          footer={(
            <>
              <Button onClick={() => setConfirmSignOut(false)}>Cancel</Button>
              <Button
                variant="danger"
                disabled={busy}
                onClick={() => { setConfirmSignOut(false); void signOutEverywhere() }}
              >
                Sign out everywhere
              </Button>
            </>
          )}
        >
          <p className="m-0 p-4 text-body leading-[1.45] text-text-muted">
            Every device signs out, including this one. Anybody with your password will
            need it again — and a second step, if you have one set up.
          </p>
        </Modal>
      )}

      {twoStep && (
        <TwoStepSetup
          begin={twoStep.begin}
          onClose={() => setTwoStep(null)}
          onChanged={setMethodsAnswer}
        />
      )}

      <AccountSecurityEditor
        action={security}
        me={me}
        anchor={securityAt.current}
        onClose={() => setSecurity(null)}
        onDone={(message) => {
          setSecurity(null)
          setNotice(message)
          onSaved?.()
          void loadSessions()
        }}
      />
    </div>
  )
}

const THEME_LABELS = { light: 'Light', dark: 'Dark', auto: 'Auto' } as const

/** The windows worth offering. Anything finer is a clock nobody wanted. */
const QUIET_WINDOWS = [
  { value: '', label: 'Off' },
  { value: '21:00-06:00', label: '9 pm – 6 am' },
  { value: '20:00-07:00', label: '8 pm – 7 am' },
  { value: '18:00-08:00', label: '6 pm – 8 am' },
]

/** "7.5 h", or an em dash when there is nothing clocked. */
function hours(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '—'
  return `${(Math.round(minutes / 6) / 10).toFixed(1)} h`
}

/**
 * The four numbers, with a dash where there is no answer yet.
 *
 * A person who has never clocked in still gets the strip: four labels and
 * four dashes say "this is where your hours will be", where a paragraph
 * explaining that said it at ten times the height.
 */
function hourStats(week: HoursWeek | null, before: HoursWeek | null): Stat[] {
  const shop = week ? Math.max(0, week.totalMinutes - week.jobMinutes) : null
  return [
    { label: 'This week', value: hours(week?.totalMinutes) },
    { label: 'Job time', value: hours(week?.jobMinutes) },
    { label: 'Shop & other', value: hours(shop) },
    { label: 'Last week', value: hours(before?.totalMinutes) },
  ]
}

/** A field that shows rather than takes — same box, no input. */
function ReadOnlyField({ label, value, after, hint }: {
  label: string; value: string; after?: string; hint?: string
}) {
  return (
    <div className="block min-w-0">
      <span className="mb-1 block text-sub text-text-muted">{label}</span>
      <span className={cn('flex h-9 w-full items-center gap-2 rounded-field border border-line',
        'bg-surface px-2.5 text-[13.5px] font-semibold text-text')}>
        <span className="min-w-0 truncate">{value}</span>
        {after && (
          <span className="ml-auto shrink-0 text-[11.5px] font-medium text-text-faint">
            {after}
          </span>
        )}
      </span>
      {hint && <span className="mt-1 block text-sub text-text-faint">{hint}</span>}
    </div>
  )
}

